import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Form, 
  Input, 
  Button, 
  Space, 
  Tag, 
  DatePicker, 
  message,
  Spin,
  Row,
  Col,
  Select,
  Statistic
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  BarChartOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { getLogs, searchLogs, getLogStats } from '../services/logService';
import { formatDateTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import locale from 'antd/es/date-picker/locale/zh_CN';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Logs = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchForm] = Form.useForm();
  const [stats, setStats] = useState({
    totalLogs: 0,
    todayLogs: 0,
    userCount: 0,
    actionTypes: []
  });
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 加载日志列表
  const loadLogs = async (params = {}) => {
    setLoading(true);
    try {
      // 合并搜索条件和分页参数
      const queryParams = {
        ...params,
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      
      // 处理日期范围
      if (params.dateRange && params.dateRange.length === 2) {
        queryParams.startDate = params.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = params.dateRange[1].format('YYYY-MM-DD');
        delete queryParams.dateRange;
      }
      
      const data = await searchLogs(queryParams);
      setLogs(data.items || []);
      setPagination({
        ...pagination,
        total: data.total || 0
      });
    } catch (error) {
      message.error(error.message || '获取日志失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 加载统计数据
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await getLogStats();
      setStats(data);
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };
  
  // 组件加载时获取日志列表和统计数据
  useEffect(() => {
    if (isAdmin()) {
      loadLogs();
      loadStats();
    }
  }, [isAdmin]);
  
  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    setPagination(pagination);
    const searchValues = searchForm.getFieldsValue();
    loadLogs(searchValues);
  };
  
  // 处理搜索
  const handleSearch = (values) => {
    setPagination({ ...pagination, current: 1 });
    loadLogs(values);
  };
  
  // 获取操作类型的标签颜色
  const getActionColor = (action) => {
    switch (action) {
      case '登录':
      case 'login':
        return 'blue';
      case '提交运单':
      case 'submit':
        return 'green';
      case '查询':
      case 'query':
        return 'purple';
      case '修改':
      case 'update':
        return 'orange';
      case '删除':
      case 'delete':
        return 'red';
      case 'view':
        return 'cyan';
      default:
        return 'default';
    }
  };
  
  // 格式化搜索参数显示
  const formatSearchParams = (params) => {
    if (!params) return '-';
    try {
      // 如果是字符串，尝试解析为JSON
      const searchParams = typeof params === 'string' ? JSON.parse(params) : params;
      return Object.entries(searchParams)
        .filter(([_, value]) => value) // 只显示非空值
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    } catch (e) {
      return String(params);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '操作时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => formatDateTime(timestamp),
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      render: (username) => username || '-',
    },
    {
      title: '操作类型',
      dataIndex: 'type',  // 使用type而不是action
      key: 'type',
      render: (type) => (
        <Tag color={getActionColor(type)}>{type}</Tag>
      ),
    },
    {
      title: '记录ID',
      dataIndex: 'recordId',
      key: 'recordId',
    },
    {
      title: '详细信息',
      dataIndex: 'searchParams',
      key: 'searchParams',
      ellipsis: true,
      render: (searchParams) => formatSearchParams(searchParams),
    },
    {
      title: 'IP地址',
      dataIndex: 'clientIP',  // 使用clientIP而不是ip
      key: 'clientIP',
    },
    {
      title: 'IP位置',
      dataIndex: 'ip_location',
      key: 'ip_location',
    },
  ];
  
  // 如果不是管理员，显示无权限信息
  if (!isAdmin()) {
    return (
      <Card>
        <Title level={4}>无权访问</Title>
        <p>您没有管理员权限，无法访问系统日志。</p>
      </Card>
    );
  }
  
  return (
    <div className="logs-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总日志数"
              value={stats.totalLogs || 0}
              prefix={<FileTextOutlined />}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="今日操作"
              value={stats.todayLogs || 0}
              prefix={<ClockCircleOutlined />}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="活跃用户数"
              value={stats.userCount || 0}
              prefix={<UserOutlined />}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="操作类型数"
              value={(stats.actionTypes && Array.isArray(stats.actionTypes)) ? stats.actionTypes.length : 0}
              prefix={<BarChartOutlined />}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>
      
      <Card title={<Title level={4}>系统日志</Title>}>
        <Form
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="username" label="用户名">
                <Input placeholder="输入用户名" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="type" label="操作类型">
                <Select placeholder="选择操作类型" allowClear>
                  {/* 使用实际的actionTypes或默认类型 */}
                  {(stats.actionTypes && stats.actionTypes.length > 0) ? 
                    stats.actionTypes.map(action => (
                      <Option key={action} value={action}>{action}</Option>
                    )) : 
                    [
                      <Option key="query" value="query">查询</Option>,
                      <Option key="submit" value="submit">提交运单</Option>,
                      <Option key="update" value="update">修改</Option>,
                      <Option key="delete" value="delete">删除</Option>,
                      <Option key="login" value="login">登录</Option>,
                      <Option key="view" value="view">查看</Option>
                    ]
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="clientIP" label="IP地址">
                <Input placeholder="输入IP地址" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="dateRange" label="时间范围">
                <RangePicker 
                  style={{ width: '100%' }} 
                  locale={locale}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="searchParams" label="详细信息">
                <Input placeholder="输入关键词" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SearchOutlined />}
            >
              搜索
            </Button>
          </Form.Item>
        </Form>
        
        <Spin spinning={loading}>
          <Table 
            columns={columns} 
            dataSource={logs} 
            rowKey="id"
            pagination={pagination}
            onChange={handleTableChange}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default Logs;
